/**
 * Deployment-aware cookie namespacing for Supabase auth.
 *
 * WHY THIS EXISTS:
 * Staging, preview-staging, and localhost all share the same Supabase project.
 * Without namespacing, each deployment uses identical cookie names
 * (sb-{ref}-auth-token). When deployment A refreshes its token, Supabase
 * rotates the refresh token family. Deployment B still holds the old token.
 * On its next refresh, Supabase detects "token reuse" (a theft signal) and
 * revokes the entire session — logging the user out.
 *
 * FIX: Each deployment gets a unique cookie suffix derived from
 * NEXT_PUBLIC_AUTH_COOKIE_SUFFIX. The Supabase library sees the same cookie
 * names internally, but the actual browser/response cookies are suffixed,
 * so deployments never collide.
 *
 * INDUSTRY PRECEDENT:
 * - Vercel preview deployments use __vercel_live_token_{hash} per preview URL
 * - Firebase uses sessionCookieName per environment
 * - Auth0 recommends separate applications per deployment
 *
 * CONFIGURATION:
 *   Production (app.virza.io):         not set → no suffix (backward-compatible)
 *   Staging (app-staging.virza.io):    NEXT_PUBLIC_AUTH_COOKIE_SUFFIX=stg
 *   Preview (preview-staging.virza.io): NEXT_PUBLIC_AUTH_COOKIE_SUFFIX=pvw
 *   Localhost:                          NEXT_PUBLIC_AUTH_COOKIE_SUFFIX=dev
 */

/**
 * Cookie suffix for this deployment. Empty string in production (no suffix).
 * Set via NEXT_PUBLIC_AUTH_COOKIE_SUFFIX environment variable.
 *
 * Validated against a conservative pattern (lowercase alphanumerics, hyphens,
 * and underscores only). Invalid values (whitespace, dots, semicolons, etc.)
 * would break cookie parsing or cause browsers to reject the cookie, so they
 * fall back to '' (same as production — no namespace). In non-production, we
 * warn once when a non-empty configured suffix is invalid so staging/preview
 * misconfiguration is caught early.
 */
const _rawSuffix = (process.env.NEXT_PUBLIC_AUTH_COOKIE_SUFFIX ?? '').trim();
const _SUFFIX_PATTERN = /^[a-z0-9_-]*$/;
const _hasConfiguredSuffix = _rawSuffix.length > 0;
const _isValidSuffix = _SUFFIX_PATTERN.test(_rawSuffix);
const COOKIE_SUFFIX = _isValidSuffix ? _rawSuffix : '';

if (_hasConfiguredSuffix && !_isValidSuffix && process.env.NODE_ENV !== 'production') {
  console.warn(
    '[supabase] Ignoring invalid NEXT_PUBLIC_AUTH_COOKIE_SUFFIX; expected lowercase letters, numbers, underscores, or hyphens. Cookie namespacing is disabled for this deployment.',
  );
}

/**
 * Whether cookie namespacing is active for this deployment.
 */
export const isNamespaced = COOKIE_SUFFIX.length > 0;

/**
 * Add the deployment suffix to a cookie name.
 *
 * "sb-abc-auth-token"   → "sb-abc-auth-token.stg"
 * "sb-abc-auth-token.0" → "sb-abc-auth-token.0.stg"
 *
 * Only Supabase auth cookies (starting with "sb-") are namespaced.
 * Application cookies (workspace ID, onboarding, etc.) are left unchanged
 * since they don't cause cross-deployment conflicts.
 */
export function namespaceCookieName(name: string): string {
  if (!COOKIE_SUFFIX || !name.startsWith('sb-')) return name;
  return `${name}.${COOKIE_SUFFIX}`;
}

/**
 * Strip the deployment suffix from a cookie name.
 *
 * "sb-abc-auth-token.stg"   → "sb-abc-auth-token"
 * "sb-abc-auth-token.0.stg" → "sb-abc-auth-token.0"
 *
 * Non-namespaced cookies pass through unchanged.
 */
export function denamespaceCookieName(name: string): string {
  if (!COOKIE_SUFFIX) return name;
  const suffix = `.${COOKIE_SUFFIX}`;
  if (name.endsWith(suffix)) {
    return name.slice(0, -suffix.length);
  }
  return name;
}

/**
 * Transform cookies from browser/request format to Supabase-internal format.
 *
 * Strips the deployment suffix from Supabase auth cookies (sb-*) so the
 * Supabase library sees the cookie names it expects (sb-{ref}-auth-token,
 * sb-{ref}-auth-token.0, etc.).
 *
 * Only sb-* cookies are denamespaced — non-Supabase cookies (workspace ID,
 * onboarding, etc.) are passed through untouched.
 *
 * Deduplication: when both an unsuffixed cookie (e.g. sb-abc-auth-token from
 * an old production session) and a suffixed cookie (sb-abc-auth-token.stg)
 * coexist, only the suffixed variant is used. This prevents Supabase from
 * reading an unpredictable stale token.
 */
export function denamespaceCookies(
  cookies: Array<{ name: string; value: string }>,
): Array<{ name: string; value: string }> {
  if (!COOKIE_SUFFIX) return cookies;

  const suffix = `.${COOKIE_SUFFIX}`;

  // Separate Supabase cookies from non-Supabase cookies.
  // Non-Supabase cookies are never renamed and pass through as-is.
  const sbCookies = cookies.filter(({ name }) => name.startsWith('sb-'));
  const otherCookies = cookies.filter(({ name }) => !name.startsWith('sb-'));

  // Build a set of the base names (without suffix) that have a suffixed variant,
  // so we can drop the stale unsuffixed duplicate if both are present.
  const suffixedBaseNames = new Set(
    sbCookies
      .filter(({ name }) => name.endsWith(suffix))
      .map(({ name }) => name.slice(0, -suffix.length)),
  );

  const dedupedSbCookies = sbCookies
    .filter(({ name }) => {
      // Drop an unsuffixed cookie when a suffixed version also exists —
      // prefer the deployment-specific token over a stale cross-deployment one.
      if (!name.endsWith(suffix) && suffixedBaseNames.has(name)) return false;
      return true;
    })
    .map(({ name, value }) => ({
      name: name.endsWith(suffix) ? name.slice(0, -suffix.length) : name,
      value,
    }));

  return [...dedupedSbCookies, ...otherCookies];
}

/**
 * Transform cookies from Supabase-internal format to deployment-specific format.
 *
 * Adds the deployment suffix so each deployment writes to its own cookie names.
 */
export function namespaceCookies<T extends { name: string; value: string }>(cookies: T[]): T[] {
  if (!COOKIE_SUFFIX) return cookies;
  return cookies.map((cookie) => ({
    ...cookie,
    name: namespaceCookieName(cookie.name),
  }));
}
